import { setDefaultOptions } from "date-fns";
import type { Locale } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import i18n from "i18next";

/**
 * Keeps date-fns' default locale in step with the UI language.
 *
 * App.tsx used to pin date-fns to French once at startup, so day names and
 * month headers stayed French whatever language the user picked. i18next
 * already knows the language and announces every change; date-fns just has
 * to listen.
 *
 * Resolution is by the language's first two letters, so "fr-FR" and "fr"
 * both land on the French locale, and anything unknown falls back to
 * English - the same fallback i18next uses.
 */
const LOCALES: Record<string, Locale> = {
	fr,
	en: enUS,
};

export const dateLocaleFor = (language: string | undefined): Locale =>
	LOCALES[(language ?? "").slice(0, 2).toLowerCase()] ?? enUS;

export const syncDateLocaleWithLanguage = (): void => {
	setDefaultOptions({ locale: dateLocaleFor(i18n.language) });

	i18n.on("languageChanged", (language: string) => {
		setDefaultOptions({ locale: dateLocaleFor(language) });
	});
};

/**
 * The same resolution for the amphore-lib labels (« Fermer », etc.), which
 * only know French and English.
 */
export const libLocaleFor = (language: string | undefined): "fr" | "en" =>
	(language ?? "").slice(0, 2).toLowerCase() === "fr" ? "fr" : "en";
